<template>
  <div>
    <search-sale-agent-monthly-performance
      statistic-type="additional"
      @submit="submitSearch"
    />

    <div class="simiot-table-operation-buttons">
      <a-button type="primary" @click="exportData">导出</a-button>
    </div>

    <a-table
      :scroll="{ x: 'max-content' }"
      size="middle"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :pagination="false"
      :expand-icon="expandIcon"
      :row-class-name="rowClassName"
      row-key="id"
      @change="sortChange"
    >
      <div slot="action" slot-scope="text, record">
        <a @click="recalculate(record)">重新计算</a>
      </div>

      <div slot-scope="record" slot="expandedRowRender">
        <a-table
          size="middle"
          :pagination="false"
          :columns="costGroupColumns(record)"
          :data-source="record.cost_groups || []"
          row-key="rowKey"
        />
      </div>
    </a-table>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import SearchSaleAgentMonthlyPerformance from '@/views/sale_monthly_performances/info/Search'
import { exportSaleAgentMonthlyAdditionalPerformances, findSaleAgentMonthlyAdditionalPerformances, recalculateSaleMonthlyPerformance } from '@/api/sale_monthly_performance'
import { exportExcel } from '@/api/excel'
import { formatCurrency, formatBigNumber } from '@/utils/filter'
import { agentAdditionalFloorPairs, amountColumn, canViewSaleCostProfit, cardFloorDiff, floorStatColumns, hasNegativeFloorDiff, packageFloorDiff, salePerformanceRecalculateContent } from '@/utils/sale_monthly_performance'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'SaleMonthlyPerformanceInfoAdditionalList',
  components: {
    Pagination,
    SearchSaleAgentMonthlyPerformance
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      data: [],
      loading: true,
      pagination: {
        total_count: 0
      },
      query: {},
      sort: {
        sort_field: '',
        sort_order: ''
      }
    }
  },
  computed: {
    canViewCostProfit() {
      return canViewSaleCostProfit(this.$store.getters.userRole)
    },
    canRecalculate() {
      return hasPermission('sale_monthly_performances/recalculate')
    },
    columns() {
      const cols = [
        {
          title: '客户名称',
          dataIndex: 'agent_name'
        },
        {
          title: '账单类型',
          dataIndex: 'bill_type'
        },
        {
          title: '项目批次',
          dataIndex: 'agent_bill_no'
        }
      ]
      if (this.canRecalculate) {
        cols.push({
          title: '操作',
          width: 100,
          scopedSlots: { customRender: 'action' }
        })
      }
      cols.push(
        amountColumn('账单金额(元)', 'amount', formatCurrency, this.sort)
      )
      if (this.canViewCostProfit) {
        cols.push(
          amountColumn('成本核算(元)', 'cost', formatCurrency, this.sort),
          amountColumn('毛利核算(元)', 'profit', formatCurrency, this.sort)
        )
      }
      cols.push(
        {
          title: '卡数',
          dataIndex: 'cards_count'
        },
        amountColumn('套餐费用(元)', 'product_amount', formatCurrency, this.sort),
        amountColumn('返佣费用(元)', 'rebate_fee', formatCurrency, this.sort),
        amountColumn('套餐底价(元)', 'floor_amount', formatCurrency, this.sort),
        {
          title: '套餐底价差(元)',
          customRender: (text, record) => formatCurrency(packageFloorDiff(record.product_amount, record.floor_amount, record.rebate_fee)),
          dataIndex: 'sale_profit',
          sorter: true,
          sortOrder: this.sort.sort_field === 'sale_profit' ? this.sort.sort_order : false
        },
        amountColumn('卡板费用(元)', 'card_amount', formatCurrency, this.sort),
        amountColumn('卡板底价(元)', 'card_floor_amount', formatCurrency, this.sort),
        {
          title: '卡板底价差(元)',
          customRender: (text, record) => formatCurrency(cardFloorDiff(record.card_amount, record.card_floor_amount)),
          dataIndex: 'card_floor_diff',
          sorter: true,
          sortOrder: this.sort.sort_field === 'card_floor_diff' ? this.sort.sort_order : false
        },
        {
          title: 'eSIM数量',
          dataIndex: 'esims_count'
        },
        amountColumn('eSIM费用(元)', 'esim_amount', formatCurrency, this.sort),
        ...floorStatColumns(formatCurrency, this.sort, 'esim_floor_amount', 'esim_floor_diff', 'eSIM底价(元)', 'eSIM底价差(元)', 'esim_amount'),
        {
          title: 'Profile数量',
          dataIndex: 'profiles_count'
        },
        amountColumn('Profile费用(元)', 'profile_amount', formatCurrency, this.sort),
        ...floorStatColumns(formatCurrency, this.sort, 'profile_floor_amount', 'profile_floor_diff', 'Profile底价(元)', 'Profile底价差(元)', 'profile_amount'),
        {
          title: '模组数量',
          dataIndex: 'modules_count'
        },
        amountColumn('模组费用(元)', 'module_amount', formatCurrency, this.sort),
        ...floorStatColumns(formatCurrency, this.sort, 'module_floor_amount', 'module_floor_diff', '模组底价(元)', '模组底价差(元)', 'module_amount'),
        {
          title: 'CPE数量',
          dataIndex: 'cpes_count'
        },
        amountColumn('CPE费用(元)', 'cpe_amount', formatCurrency, this.sort),
        ...floorStatColumns(formatCurrency, this.sort, 'cpe_floor_amount', 'cpe_floor_diff', 'CPE底价(元)', 'CPE底价差(元)', 'cpe_amount'),
        amountColumn('快递费(元)', 'shipping_fee', formatCurrency, this.sort),
        {
          title: '账单时间',
          dataIndex: 'agent_bill_time'
        }
      )
      return cols
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    submitSearch(search) {
      this.sort = this.$options.data().sort
      this.query = Object.assign({},
        this.$options.data().query,
        { per_page: this.query.per_page },
        search)
      this.fetchData()
    },

    sortChange(pagination, filters, sorter) {
      this.sort.sort_field = sorter.field || ''
      this.sort.sort_order = sorter.order || ''
      this.query.page = 1
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      findSaleAgentMonthlyAdditionalPerformances(this.id, Object.assign({}, this.query, this.sort)).then((res) => {
        if (res.code === 0) {
          this.data = (res.data || []).map((row) => {
            const groups = (row.cost_groups || []).map((g, gi) => ({
              ...g,
              rowKey: `${row.id}-${g.supplier_id}-${g.product_model_id || 0}-${g.unit_price}-${gi}`
            }))
            return {
              ...row,
              cost_groups: row.cost_groups ? groups : undefined
            }
          })
          this.pagination = res.pagination
        }
        this.loading = false
      })
    },

    costGroupColumns(record) {
      const cols = [
        {
          title: '供应商',
          dataIndex: 'supplier_name'
        }
      ]
      if (record.bill_type === '模组采购账单' || record.bill_type === 'CPE采购账单') {
        cols.push({
          title: '产品型号',
          dataIndex: 'product_model_name'
        })
      }
      cols.push(
        {
          title: '成本单价(元)',
          customRender: formatCurrency,
          dataIndex: 'unit_price'
        },
        {
          title: '数量',
          customRender: formatBigNumber,
          dataIndex: 'count'
        },
        {
          title: '成本小计(元)',
          customRender: formatCurrency,
          dataIndex: 'cost_subtotal'
        }
      )
      return cols
    },

    rowClassName(record) {
      if (hasNegativeFloorDiff(record, agentAdditionalFloorPairs)) {
        return 'sale-profit-negative-row'
      }
      if (packageFloorDiff(record.product_amount, record.floor_amount, record.rebate_fee) < 0) {
        return 'sale-profit-negative-row'
      }
      if (this.canViewCostProfit && record.profit != null && Number(record.profit) < 0) {
        return 'sale-profit-negative-row'
      }
      return ''
    },

    exportData() {
      exportSaleAgentMonthlyAdditionalPerformances(this.id, Object.assign({}, this.query, this.sort)).then((res) => {
        if (res.code === 0 && res.data.is_direct_download) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    },

    recalculate(record) {
      this.$confirm({
        title: '重新计算该账单的销售业绩？',
        content: salePerformanceRecalculateContent({ useLatestFloorPrice: true }),
        onOk: () => recalculateSaleMonthlyPerformance({
          sale_agent_monthly_performance_id: record.id
        })
      })
    },

    expandIcon({ expanded, expandable, record, onExpand }) {
      if (!this.canViewCostProfit || !Array.isArray(record.cost_groups)) {
        return null
      }
      return (
        <a onClick={e => onExpand(record, e)}>
          {expanded ? <a-icon type='minus-square' /> : <a-icon type='plus-square' />}
        </a>
      )
    }
  }
}
</script>

<style>
.sale-profit-negative-row td {
  color: #f5222d !important;
}
</style>
